type RequestOptions = {
  json?: any;
  formData?: FormData;
  headers?: Record<string, string>;
};

type ApiResponse<T = any> = Response & {
  json: () => Promise<T>;
};

class ApiRequest<T = any> implements PromiseLike<ApiResponse<T>> {
  private request: Promise<Response>;
  private jsonData: Promise<T> | null = null;
  private options: RequestOptions;

  constructor(request: Promise<Response>, options: RequestOptions = {}) {
    this.request = request;
    this.options = options;
  }

  headers(headers: Record<string, string>): ApiRequest<T> {
    const newOptions = {
      ...this.options,
      headers: { ...this.options.headers, ...headers },
    };
    return this.clone(newOptions);
  }

  withJson(data: any): ApiRequest<T> {
    const newOptions = { ...this.options, json: data };
    return this.clone(newOptions);
  }

  private clone(newOptions: RequestOptions): ApiRequest<T> {
    // Recreate the request with new options
    const newRequest = fetch(this.getUrl(), {
      method: this.getMethod(),
      headers: newOptions.headers,
      body: newOptions.json ? JSON.stringify(newOptions.json) : undefined,
    });

    return new ApiRequest<T>(newRequest, newOptions);
  }

  // Helper methods to get current request info
  private getUrl(): string {
    // You'll need to store the URL when creating the request
    return (this.request as any)._url || '';
  }

  private getMethod(): string {
    // You'll need to store the method when creating the request
    return (this.request as any)._method || 'GET';
  }

  then<TResult1 = ApiResponse<T>, TResult2 = never>(
    onfulfilled?:
      | ((value: ApiResponse<T>) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    return this.request.then(async (response) => {
      const enhancedResponse: ApiResponse<T> = Object.assign(response, {
        json: () => {
          if (!this.jsonData) {
            this.jsonData = response.json();
          }
          return this.jsonData;
        },
      });
      return onfulfilled
        ? onfulfilled(enhancedResponse)
        : (enhancedResponse as any);
    }, onrejected);
  }

  async json<R = T>(): Promise<R> {
    const response = await this.request;
    if (!this.jsonData) {
      this.jsonData = response.json();
    }
    // @ts-expect-error -- Promise<T> not assignable to Promise<R> when R overrides T at the call site
    return this.jsonData as Promise<R>;
  }
}

const createRequest = (
  url: string,
  method: string,
  options?: RequestOptions,
): ApiRequest => {
  let body: string | FormData | undefined = undefined;
  const headers: Record<string, string> = {
    ...options?.headers,
  };

  if (options?.formData) {
    body = options.formData;
  } else if (options?.json) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.json);
  }

  const request = fetch(url, {
    method,
    headers,
    body,
  });

  // Store URL and method for cloning
  (request as any)._url = url;
  (request as any)._method = method;

  return new ApiRequest(request, options);
};

export const api = {
  headers: (headers: Record<string, string>) => ({
    get: <T = any>(url: string, options?: RequestOptions) =>
      createRequest(url, 'GET', { ...options, headers }) as ApiRequest<T>,
    post: <T = any>(url: string, options?: RequestOptions) =>
      createRequest(url, 'POST', { ...options, headers }) as ApiRequest<T>,
    put: <T = any>(url: string, options?: RequestOptions) =>
      createRequest(url, 'PUT', { ...options, headers }) as ApiRequest<T>,
    delete: <T = any>(url: string, options?: RequestOptions) =>
      createRequest(url, 'DELETE', { ...options, headers }) as ApiRequest<T>,
  }),
  get: <T = any>(url: string, options?: RequestOptions) =>
    createRequest(url, 'GET', options) as ApiRequest<T>,
  post: <T = any>(url: string, options?: RequestOptions) =>
    createRequest(url, 'POST', options) as ApiRequest<T>,
  put: <T = any>(url: string, options?: RequestOptions) =>
    createRequest(url, 'PUT', options) as ApiRequest<T>,
  delete: <T = any>(url: string, options?: RequestOptions) =>
    createRequest(url, 'DELETE', options) as ApiRequest<T>,
};
